import type { MealMenu } from "@/data/meal-plan";
import { cn } from "@/lib/utils";

interface MealMenuPickerProps {
  menus: MealMenu[];
  value: string | undefined;
  onSelect: (menuId: string) => void;
}

/**
 * Pick-one control for meals made of fixed menus. With nothing chosen it
 * lists every menu with its summary; once chosen it collapses to a segmented
 * control plus the chosen menu's summary.
 */
export function MealMenuPicker({ menus, value, onSelect }: MealMenuPickerProps) {
  const selected = menus.find((m) => m.id === value);

  if (!selected) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">Elige una opción</p>
        <div className="grid gap-2">
          {menus.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onSelect(m.id)}
              className="w-full text-left rounded-lg border px-3 py-2 hover:bg-muted transition-colors"
            >
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-xs text-muted-foreground">{m.summary}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div
        role="radiogroup"
        aria-label="Opción"
        className="grid grid-flow-col auto-cols-fr gap-1 rounded-lg bg-muted p-1"
      >
        {menus.map((m) => {
          const active = m.id === selected.id;
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onSelect(m.id)}
              className={cn(
                "truncate rounded-md px-1 py-1 text-xs font-medium transition-colors",
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {m.name}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{selected.summary}</p>
    </div>
  );
}
